import type { DoorDescriptor } from "building-types/doors/doorTypes";
import { SwingSide } from "building-types/doors/doorTypes";
import type { GarageDoorFuncOptions } from "building-types/openings/openingFuncOptions";
import type { Room } from "building-types/rooms/roomTypes";
import { AwningType } from "building-types/settings/settingsTypesEnum";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { defaultDoorCatalog } from "doc/elements/doors/doorCatalog";
import getMergedDoorSettings from "doc/elements/doors/getMergedDoorSettings";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLine2ByFaceEdge } from "geom/planarGraph";
import { polyLine2EdgeLength } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dot, vec2Len, vec2Normalize, vec2Sub } from "geom/vec2";
import { generatedDoorPlacementDeprecatedNew, doorDescriptorComputeHeight } from "../../doors/door";
import { computeOpeningZOffset } from "../openingPlacement";
import type { OpeningPlacementFuncOptions } from "../openingPlacementFuncOptions";
import { windowPlacementFunc } from "./windowPlacementFunc";

export function garageDoorPlacementFunc({
  garageThreeCars = false,
  awning,
  opts,
}: GarageDoorFuncOptions & { opts: OpeningPlacementFuncOptions }) {
  const { roomStart, roomEnd, halfEdgeIndex, planarGraph, roomLevelIndices } = opts;

  // for each edge of the room, find the edges that are facing outside
  const roomLevelId = roomStart?.id ? roomLevelIndices[roomStart.id] : undefined;
  const { faceWindingDirs, faceHalfEdges, halfEdgeParentFace } = planarGraph.planarGraph;
  const { polygonToPlanarGraph } = planarGraph;

  enum GarageEdgeType {
    FRONT,
    SIDE,
    BACK,
    TOO_SMALL,
  }

  const edgePriorities: {
    [key in GarageEdgeType]?: number;
  } = {
    [GarageEdgeType.TOO_SMALL]: 3,
  };

  const exteriorEdges: { halfEdgeIndex: number; line2: PolyLine2; edgeType: GarageEdgeType }[] = [];
  assertIsNotNullOrUndefined(roomLevelId);
  const roomEdges = polygonToPlanarGraph[roomLevelId];

  // for each wall
  function classifyRectEdge(line2: PolyLine2) {
    const [edgeDirX, edgeDirY] = vec2Sub(line2[2], line2[3], line2[0], line2[1]);
    const [ndx, ndy] = vec2Normalize(edgeDirX, edgeDirY);

    if (vec2Len(edgeDirX, edgeDirY) < 96) {
      return GarageEdgeType.TOO_SMALL;
    }

    const d = vec2Dot(1, 0, ndx, ndy);

    if (Math.abs(d - 1.0) < DIST_EPSILON) {
      return GarageEdgeType.FRONT;
    } else if (Math.abs(d) < DIST_EPSILON) {
      return GarageEdgeType.SIDE;
    } else {
      return GarageEdgeType.BACK;
    }
  }

  for (let i = 0, li = roomEdges.length; i < li; i++) {
    const subWalls = roomEdges[i];

    // for each sub-edge of the wall
    for (let j = 0, lj = subWalls.length; j < lj; j++) {
      const { index, edgeIndex } = subWalls[j];

      const halfEdge = faceHalfEdges[index][edgeIndex];
      const adjHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdge);

      if (faceWindingDirs[halfEdgeParentFace[adjHalfEdgeIndex]] === WindingDir.CW) {
        // faces outside
        const line2 = planarGraphHalfEdgeLine2ByFaceEdge(planarGraph.planarGraph, index, edgeIndex);
        exteriorEdges.push({ halfEdgeIndex: halfEdge, line2, edgeType: classifyRectEdge(line2) });
      }
    }
  }

  // sort the edges - the first edge should hopefully match the request garage edge
  exteriorEdges.sort((a, b) => {
    const priorityA = edgePriorities[a.edgeType];
    const priorityB = edgePriorities[b.edgeType];
    if (priorityA === undefined || priorityB === undefined) {
      return 0;
    }
    return priorityA - priorityB;
  });

  if (exteriorEdges[0]?.line2 && polyLine2EdgeLength(exteriorEdges[0].line2, 0) > 300) {
    garageThreeCars = true;
  }

  function garageDoorNew(descriptor: DoorDescriptor, edgeParam: number, edgeParamNormalized: boolean) {
    const doorBaseOffset = computeOpeningZOffset(opts.roomFloorAssemblies, opts.roomStart, opts.roomEnd);
    descriptor = doorDescriptorComputeHeight(descriptor, doorBaseOffset, opts.doorHomeSettings.door);

    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
    const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

    const { activeSettingsSets, settings, entitySettings, optionChoices } = opts.mergeSettingsForOpeningParams;

    const { exteriorCasing } = getMergedDoorSettings({
      homeSettings: opts.doorHomeSettings,
      activeSettingsSets,
      entitySettings,
      optionChoices,
      descriptor,
      settings,
    });

    return generatedDoorPlacementDeprecatedNew({
      descriptor,
      frontRoom: roomStart as Room,
      backRoom: roomEnd,
      halfEdgeIndex,
      param: edgeParam,
      paramNormalized: edgeParamNormalized,
      swingSide: SwingSide.NONE,
      awning,
      exteriorCasing,
      interiorCasing: undefined,
      awningSettings: { awningType: AwningType.DISABLED },
      streetOrientation,
    });
  }

  if (exteriorEdges[0] && exteriorEdges[0].edgeType < 3 && exteriorEdges[0].halfEdgeIndex === halfEdgeIndex) {
    if (polyLine2EdgeLength(exteriorEdges[0].line2, 0) > 300 && garageThreeCars) {
      // Place two garage doors with a small buffer between them and the end of the wall.
      // The constants here are arbitrary and ensure the doors are non-overlapping.
      return {
        doorPlacements: [
          garageDoorNew(defaultDoorCatalog.G1, defaultDoorCatalog.G1.width / 2 + 12, false),
          garageDoorNew(
            defaultDoorCatalog.G2,
            defaultDoorCatalog.G1.width + defaultDoorCatalog.G2.width / 2 + 18,
            false,
          ),
        ],
      };
    } else if (polyLine2EdgeLength(exteriorEdges[0].line2, 0) < 144) {
      return {
        doorPlacements: [garageDoorNew(defaultDoorCatalog.G1, 0.5, true)],
      };
    }
    return {
      doorPlacements: [garageDoorNew(defaultDoorCatalog.G2, 0.5, true)],
    };
  }

  return windowPlacementFunc(opts);
}
